"use client";

import { LuClock8 } from "react-icons/lu";
import { Input } from "../input";
import { cn } from "@/lib/utils";
import React, { useMemo, useState } from "react";
import { formatDate, parse } from "date-fns";
import { useDateLocale } from "@/app/[lng]/_components/datelocale-context";

//TODO: Fix uncaught client errors when inputting wrong times
type Props = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "defaultValue" | "onChange"
> & {
  id?: string;
  defaultValue?: Date;
  className?: string;
  inputClassName?: string;
  smallestTimeVisible?: "hours" | "minutes" | "seconds";
  onChange?: (time: Date) => void;
};

const TimePicker = React.forwardRef<HTMLInputElement, Props>(
  (
    {
      id,
      defaultValue,
      className,
      inputClassName,
      smallestTimeVisible,
      onChange,
      ...props
    },
    ref,
  ) => {
    const IntlLocale = useDateLocale();

    const stringFormat = useMemo(
      () =>
        smallestTimeVisible === "hours"
          ? "p"
          : smallestTimeVisible === "minutes"
            ? "p"
            : "pp",
      [smallestTimeVisible],
    );
    const parseFormat = useMemo(
      () =>
        smallestTimeVisible === "hours"
          ? "HH"
          : smallestTimeVisible === "minutes"
            ? "HH:mm"
            : "HH:mm:ss",
      [smallestTimeVisible],
    );
    const contextDate = useMemo(
      () => defaultValue ?? Date.now(),
      [defaultValue],
    );
    const step = useMemo(
      () =>
        smallestTimeVisible === "hours"
          ? "720"
          : smallestTimeVisible === "minutes"
            ? "60"
            : "1",
      [smallestTimeVisible],
    );
    const [time, setTime] = useState(
      defaultValue
        ? formatDate(defaultValue, stringFormat, { locale: IntlLocale })
        : "",
    );
    return (
      <div className={cn("relative", className)}>
        <div className="pointer-events-none absolute inset-y-0 start-0 flex items-center justify-center ps-3 text-secondary peer-disabled:opacity-50">
          <LuClock8 className="h-5 w-5 stroke-secondary/90" />
          <span className="sr-only">User</span>
        </div>
        {/* Maybe build a more adjustable time input using the HTML time element in a button and a popover with hour and minute scroll areas*/}
        <Input
          {...props}
          ref={ref}
          type="time"
          id={id ? id : "time-picker"}
          step={step}
          value={time}
          onChange={(event) => {
            console.log("Value: " + event.target.value.toString());
            setTime(event.target.value);
            console.log("Format: " + stringFormat);
            console.log("Context date: " + contextDate.toString());
            const parsedDate = parse(
              event.target.value,
              parseFormat,
              contextDate,
            );
            console.log("New date is " + parsedDate.toString());
            onChange?.(parsedDate);
          }}
          className={cn(
            "peer appearance-none pl-10 text-secondary [&::-webkit-calendar-picker-indicator]:hidden [&::-webkit-calendar-picker-indicator]:appearance-none",
            inputClassName,
          )}
        />
      </div>
    );
  },
);

TimePicker.displayName = "TimePicker";

export { TimePicker };
